/**
 * Auth service — login, register, token management.
 *
 * Emits 'sn:auth-change' CustomEvent on login/logout/register so that the
 * projectStore (and any other auth-aware stores) can re-sync in-memory state
 * without requiring a hard page reload.
 */

const TOKEN_KEY = 'sn:auth-token';
const USER_KEY = 'sn:auth-user';
const PROJECTS_KEY = 'sn:projects';
const ACTIVE_PROJECT_KEY = 'sn:active-project';

function emitAuthChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sn:auth-change'));
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser(): { id: string; email: string; name: string; role: string } | null {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Clear project cache to prevent cross-user data-visibility leaks.
  localStorage.removeItem(PROJECTS_KEY);
  localStorage.removeItem(ACTIVE_PROJECT_KEY);
  emitAuthChange();
  window.location.href = '/login';
}

export async function login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
  try {
    const r = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      signal: AbortSignal.timeout(5000),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      return { success: false, error: err.error || 'Login failed' };
    }
    const data = await r.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    // Clear the previous user's project cache so the next sync is clean.
    localStorage.removeItem(PROJECTS_KEY);
    localStorage.removeItem(ACTIVE_PROJECT_KEY);
    emitAuthChange();
    return { success: true };
  } catch {
    return { success: false, error: 'Server not reachable' };
  }
}

export async function register(email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> {
  try {
    const r = await fetch('/api/v1/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
      signal: AbortSignal.timeout(5000),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      return { success: false, error: err.error || 'Registration failed' };
    }
    const data = await r.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    localStorage.removeItem(PROJECTS_KEY);
    localStorage.removeItem(ACTIVE_PROJECT_KEY);
    emitAuthChange();
    return { success: true };
  } catch {
    return { success: false, error: 'Server not reachable' };
  }
}
